import type { FormDataAdapter } from "$lib/util/model-adapters"
import { PatchBuilder } from "$lib/util/model-adapters/patch.ts"
import {
  type AccountOpeningApplication,
  MARGIN_TYPES,
  ValidateYup,
} from "@tastyworks/tastyworks-api"
import { type InferType, mixed, object } from "yup"

export const MARGIN_TYPES_VALUES = Object.freeze(Object.values(MARGIN_TYPES))

export const tradingMethodSchema = ValidateYup.buildSchema(
  "TradingMethod",
  object({
    tradingMethod: mixed<MARGIN_TYPES>()
      .oneOf(Object.values(MARGIN_TYPES))
      .required(),
  })
)

export const tradingMethodAdapter = {
  toForm: (application, formData) => {
    formData.tradingMethod = application.marginOrCash as MARGIN_TYPES
    return formData
  },
  toPatch: (data) =>
    new PatchBuilder<AccountOpeningApplication>()
      .string("marginOrCash", data.tradingMethod)
      .build(),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof tradingMethodSchema>
  >,
  "toForm" | "toPatch"
>

if (import.meta.vitest) {
  const { it, expect, describe } = import.meta.vitest

  describe("tradingMethodSchema", () => {
    it("validates correctly", async () => {
      const input = {
        tradingMethod: MARGIN_TYPES.CASH,
      }

      const result = await tradingMethodSchema.validate(input)

      expect(result).toEqual(input)
    })

    it("throws when tradingMethod isn't provided", async () => {
      await expect(() => tradingMethodSchema.validate({})).rejects.toThrow()
    })

    it("throws when tradingMethod isn't an accepted type", async () => {
      const input = {
        tradingMethod: "ABC",
      }

      await expect(() => tradingMethodSchema.validate(input)).rejects.toThrow()
    })
  })

  describe("tradingMethodAdapter", () => {
    it("fills the trading method from the application", async () => {
      const { accountOpeningApplicationFactory } =
        await import("@tastyworks/tastyworks-api/factories/account-opening-application")
      const application = accountOpeningApplicationFactory.build({
        marginOrCash: MARGIN_TYPES.CASH,
      })

      expect(tradingMethodAdapter.toForm(application, {})).toEqual({
        tradingMethod: MARGIN_TYPES.CASH,
      })
    })

    it("patches the trading method", () => {
      expect(
        tradingMethodAdapter.toPatch({ tradingMethod: MARGIN_TYPES.MARGIN })
      ).toEqual({ marginOrCash: MARGIN_TYPES.MARGIN })
    })
  })
}
